import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { Logger } from 'src/app/core/logger.service';

const logKey = 'PlC Convertor Api';
const log = new Logger('Service ' + logKey);

export interface IBaseAPIService {
    GET(url: string, params?): Observable<any>;
    POST(url: string, body, params?): Observable<any>;
    PATCH(url: string, body, params?): Observable<any>;
    PUT(url: string, body, params?): Observable<any>;
    DELETE(url: string, params?): Observable<any>;
}

@Injectable()
export class BaseAPIService implements IBaseAPIService {
    constructor(private readonly http: HttpClient, private readonly authenticationService: AuthenticationService) { }

    /**
     * GET function makes a rest api call with get method and returns the Observable.
     * @param url :string - Request url string
     */

    public GET(url: string, _params?) {
        const headers: HttpHeaders = new HttpHeaders({ Authorization: 'Bearer ' + this.token });
        return this.http.get(url, { headers, responseType: 'json' /*,observe: "response"*/ })
        .pipe(catchError(e => throwError(this.errorHandler(e))));
    }

    public GET_TEXT(url: string, _params?) {
        const headers: HttpHeaders = new HttpHeaders({ Authorization: 'Bearer ' + this.token });
        return this.http.get(url, { headers, responseType: 'text' /*,observe: "response"*/ })
        .pipe(catchError(e => throwError(this.errorHandler(e))));
    }

    /**
     * POST function makeas a rest API call with post method and returns the Observable
     * @param url :string - Request url String
     * @param data - Data Object
     */

    public POST(url: string, body, _params?): Observable<any> {
        const two = 2;
        const headers: HttpHeaders = new HttpHeaders({
            Authorization: 'Bearer ' + this.token,
            'Content-Type': 'application/json',
            Accept: 'application/json'
        });
        if(url.indexOf('/api/File/UploadFileToBLOB') >= 0 ){
        return this.http.post(url, body, { headers })
        .pipe(retry(two), catchError(e => throwError(this.errorHandler(e))));
        }
        return this.http.post(url, body, { headers })
        .pipe(catchError(e => throwError(this.errorHandler(e))));
    }

    public POST_USER(url: string, paramDesc, body, _params?): Observable<any> {
        const headers: HttpHeaders = new HttpHeaders({
            Authorization: 'Bearer ' + this.token,
            description: paramDesc,
            'Content-Type': 'application/json',
            Accept: 'application/json'
        });

        return this.http.post(url, body, { headers })
        .pipe(catchError(e => throwError(this.errorHandler(e))));
        // .pipe(catchError(this.handleError));
    }

    /**
     * PATCH function makeas a rest API call with post method and returns the Observable
     * @param url :string - Request url String
     * @param data  - Data Object
     */

    public PATCH(url: string, body, _params?): Observable<any> {
        const headers: HttpHeaders = new HttpHeaders({ Authorization: 'Bearer ' + this.token });
        return this.http.patch(url, body, { headers, responseType: 'json' })
        .pipe(catchError(e => throwError(this.errorHandler(e))));
    }

    public PATCH_USER(url: string, paramDesc, body, _params?): Observable<any> {
        const headers: HttpHeaders = new HttpHeaders({
            Authorization: 'Bearer ' + this.token,
            description: paramDesc,
            'Content-Type': 'application/json',
            Accept: 'application/json'
        });

        return this.http.patch(url, body, { headers })
        .pipe(catchError(e => throwError(this.errorHandler(e))));
        // .pipe(catchError(this.handleError));
    }

    /**
     * PUT function makeas a rest API call with put method and returns the Observable
     * @param url :string - Request url String
     * @param data - Data Object
     */

    public PUT(url: string, body, _params?): Observable<any> {
        const headers: HttpHeaders = new HttpHeaders({ Authorization: 'Bearer ' + this.token });
        return this.http.put(url, body, { headers, responseType: 'json' })
        .pipe(catchError(e => throwError(this.errorHandler(e))));

    }

    /**
     * DELETE function makeas a rest API call with delete method and returns the Observable
     * @param url :string - Request url String
     */
    public DELETE(url: string, _params?): Observable<any> {
        const headers: HttpHeaders = new HttpHeaders({ Authorization: 'Bearer ' + this.token,
        'Content-Type': 'application/json',
        Accept: 'application/json' });
        return this.http.delete(url, { headers, responseType: 'json' })
        .pipe(catchError(e => throwError(this.errorHandler(e))));
    }

    /**
     * DELETESESSION function makeas a rest API call with delete method and returns the Observable
     * @param url :string - Request url String
     */
    public DELETESESSION(url: string, params?): Observable<any> {
        const headers: HttpHeaders = new HttpHeaders({ AccessToken: params, 'Access-Control-Allow-Origin':'*' });
        return this.http.delete(url, {headers, responseType: 'json' })
        .pipe(catchError(e => throwError(this.errorHandler(e))));
    }

    /**
     * DOWNLOADGet function makeas a rest API call with delete method and returns the Observable
     * @param url :string - Request url String
     */
    public DOWNLOADGet(url: string, _params?): Observable<Blob> {
        const headers: HttpHeaders = new HttpHeaders({
            'Authorization': 'Bearer ' + this.token,
            'Accept': 'application/json'
        });
        return this.http.get(url, { headers: headers, responseType: "blob" }).pipe(catchError(e => throwError(this.errorHandler(e))));
    }
     /**
     * POST function makeas a rest API call with post method and returns the Observable
     * @param url :string - Request url String
     * @param body - Data Object
     */
    public POST_Files(url: string, body, _params?): Observable<any> {
        const headers: HttpHeaders = new HttpHeaders({
            Authorization: 'Bearer ' + this.token
        });

        return this.http.post(url, body, { headers })
        .pipe(catchError(e => throwError(this.errorHandler(e))));
    }

    /**
     * handleError function handles the API exceptions
     * @param error
     */

    private readonly errorHandler = (error) => {
        const three = 3;
        const twoHundred = 200;
        const threeHundred = 300;
        const message = error.status ? `${error.status} - ${error.statusText}` : 'Server error';
        if (error.type === three) {
            log.error('Unable to reach Api Server... ');
        } else if (error.status < twoHundred || error.status >= threeHundred) {
            log.error('Request has failed ' + message);
        } else {
            log.error(error);
        }
        return error;
    }

    /**
     * buildUrl function builds the complete url(interpolation)
     * @url : API url
     * @param : input parameters
     */

  get token(): string {
    const token = sessionStorage.getItem('token') ? sessionStorage.getItem('token').replace('"', '') : '';
    if (token === '') {
          log.error('token not available... login again');
      }
    return token;
  }
}
